<template>
  <transition name="base-scroll-top">
    <button
      v-show="shown"
      type="button"
      class="base-scroll-top"
      :aria-label="$t('table.phone-list.back-to-top')"
      @click="scrollToTop"
    >
      <BaseIcon name="arrow-up" aria-hidden="true" />
    </button>
  </transition>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';

// Shows once the reader is this many screens down a long list.
const SCREENS = 2;

// A button back to the top of a long list. The page scrolls in the layout's content, not the
// window, so a phone's own shortcut (tapping the status bar on iOS) doesn't reach it.
export default {
  name: 'BaseScrollTop',
  components: { BaseIcon },
  props: {
    // The element the page scrolls in.
    scroller: {
      type: String,
      default: '.base-layout__content',
    },
  },
  data() {
    return {
      shown: false,
    };
  },
  methods: {
    // Kept-alive pages share the scroller: only the one on screen listens.
    listen() {
      const el = this.$el && this.$el.closest && this.$el.closest(this.scroller);
      if (!el || this.el === el) return;
      this.el = el;
      el.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    },
    stopListening() {
      if (!this.el) return;
      this.el.removeEventListener('scroll', this.onScroll);
      cancelAnimationFrame(this.frame);
      this.el = null;
    },
    onScroll() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(this.update);
    },
    update() {
      if (!this.el) return;
      this.shown = this.el.scrollTop > this.el.clientHeight * SCREENS;
    },
    scrollToTop() {
      if (!this.el) return;
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.el.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    },
  },
  mounted() {
    this.listen();
  },
  activated() {
    this.listen();
  },
  deactivated() {
    this.stopListening();
  },
  beforeDestroy() {
    this.stopListening();
  },
};
</script>

<style lang="scss">
.base-scroll-top {
  position: fixed;
  right: 16px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  // Over the list, under the sheets and dialogs.
  z-index: $z-index-header;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid $color-border;
  border-radius: $radius-full;
  background: $color-white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  color: $color-text-body;

  .base-icon {
    width: 20px;
    height: 20px;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px $color-accent;
  }
}

.base-scroll-top-enter-active,
.base-scroll-top-leave-active {
  transition: opacity 150ms ease, transform 150ms ease;
}

.base-scroll-top-enter,
.base-scroll-top-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
